/**
 * HLS playback bootstrapper for the admin embedded player (TASK-054, ADR-008).
 *
 * Two code paths are supported, picked at runtime per environment:
 *
 *  1. hls.js (Chrome / Firefox / Edge / Android Chrome).
 *     The instance is configured with `xhrSetup` so the admin Bearer token is
 *     attached to every internal fetch — both the master m3u8 (when hls.js
 *     fetches it; we let hls.js handle the m3u8 download so child playlists
 *     and EXT-X-KEY URIs all share the same auth hook) and the AES-128 DEK
 *     download against `/admin/tracks/:id/preview-key/:version`.
 *
 *  2. Native HLS (`canPlayType('application/vnd.apple.mpegurl')`), used by
 *     Safari macOS / Safari iPad / iOS. There is no API to inject request
 *     headers, so the loader first obtains a short-lived signed token via
 *     `fetchPreviewToken` (BUG-161, ADR-024) and hands `<audio>` the
 *     master URL with `?token=…` appended. The BE accepts the token as a
 *     standalone credential on the whole preview chain and propagates it
 *     into the rewritten child/key URLs, so the native player's follow-up
 *     requests stay authorized without any header.
 *
 * The loader returns a disposable handle exposing `destroy()` so the player
 * provider can release resources when switching tracks or unmounting.
 */

import Hls, { type ErrorData } from 'hls.js';

/** Discriminated handle returned by {@link attachHls}. */
export type HlsHandle =
  | { kind: 'hls.js'; destroy: () => void }
  | { kind: 'native'; destroy: () => void };

export interface AttachHlsArgs {
  audioEl: HTMLAudioElement;
  /** Admin endpoint URL: `/admin/tracks/:id/playback.m3u8`. Relative paths are
   *  resolved against `import.meta.env.VITE_API_BASE_URL`. */
  manifestUrl: string;
  /** In-memory admin access token (read via `useAuth().accessToken`). */
  accessToken: string;
  /** Mints a short-lived `?token=` credential for the native-HLS path
   *  (Safari/iPad — no header injection; BUG-161, ADR-024). Called lazily,
   *  only when the native path is actually taken. */
  fetchPreviewToken: () => Promise<string>;
  onFatalError: (err: HlsFatalError) => void;
}

export interface HlsFatalError {
  /** Stable code for UI messaging. */
  code:
    | 'auth'
    | 'network'
    | 'media'
    | 'native_not_available'
    | 'unknown';
  message: string;
}

/**
 * Returns true when the host can play HLS natively through `<audio>`. This
 * is the gate for the Safari/iPad fallback path.
 */
export function isNativeHlsSupported(audioEl: HTMLAudioElement): boolean {
  const canPlay = audioEl.canPlayType('application/vnd.apple.mpegurl');
  return canPlay === 'probably' || canPlay === 'maybe';
}

/**
 * Detect iPad / iPhone / iPod / Safari macOS — the browsers where hls.js
 * cannot be used (no MSE for HLS audio) and the player must fall through to
 * the native `<audio src>` path.
 *
 * Detection is intentionally conservative: only the user-agent strings we
 * actually fall through for. Anything else attempts the hls.js path first.
 */
export function shouldPreferNativeHls(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as MacIntel — disambiguate by touch points.
  const isIpadOs =
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  // Desktop Safari: contains "Safari" but not "Chrome"/"Chromium"/"Edg".
  const isSafariDesktop =
    /Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua) && !isIos && !isIpadOs;
  return isIos || isIpadOs || isSafariDesktop;
}

/**
 * Attach an HLS stream to the supplied `<audio>` element.
 *
 * Picks the native path when the browser prefers it AND can play HLS
 * natively; otherwise instantiates hls.js. Returns a handle whose `destroy()`
 * must be called on track change / unmount to release the underlying
 * `Hls` instance (memory-leak guard called out in the brief).
 */
export function attachHls(args: AttachHlsArgs): HlsHandle {
  const { audioEl, manifestUrl, accessToken, fetchPreviewToken, onFatalError } =
    args;

  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
  const absoluteManifestUrl = manifestUrl.startsWith('http')
    ? manifestUrl
    : `${baseUrl}${manifestUrl}`;

  const preferNative = shouldPreferNativeHls();
  const nativeOk = isNativeHlsSupported(audioEl);

  if (preferNative) {
    if (!nativeOk) {
      onFatalError({
        code: 'native_not_available',
        message:
          'Этот браузер не поддерживает воспроизведение HLS. Используйте Chrome или Firefox.',
      });
      return { kind: 'native', destroy: () => undefined };
    }
    return attachNative({
      audioEl,
      manifestUrl: absoluteManifestUrl,
      fetchPreviewToken,
      onFatalError,
    });
  }

  if (!Hls.isSupported()) {
    // Last-resort fallback: try the native path even if we didn't prefer it.
    if (nativeOk) {
      return attachNative({
        audioEl,
        manifestUrl: absoluteManifestUrl,
        fetchPreviewToken,
        onFatalError,
      });
    }
    onFatalError({
      code: 'native_not_available',
      message: 'Браузер не поддерживает воспроизведение HLS.',
    });
    return { kind: 'hls.js', destroy: () => undefined };
  }

  const hls = new Hls({
    enableWorker: true,
    lowLatencyMode: false,
    // BUG-078 — TimeWeb S3 cold-start handshake reliably takes >10s; hls.js
    // default fragLoadingTimeOut=10000 aborts the very first segment before
    // any byte arrives, then retries — by which time the user has given up.
    // Bumped to 30s for fragments and 20s for manifest/level loads.
    fragLoadingTimeOut: 30000,
    fragLoadingMaxRetry: 4,
    manifestLoadingTimeOut: 20000,
    levelLoadingTimeOut: 20000,
    // Inject the admin Authorization header on same-origin internal fetches
    // only — manifest, child playlist and the AES-128 key URL (the latter
    // routed by the BE to `/admin/tracks/:id/preview-key/:version?token=…`,
    // see ADR-008 §"Decision" item 3). Segment URLs are presigned and live
    // on cross-origin S3 — attaching a Bearer there is rejected with 400
    // (BUG-066).
    xhrSetup: attachAdminBearerToSameOrigin(accessToken),
  });

  const handleHlsError = (_event: unknown, data: ErrorData) => {
    if (!data.fatal) return;
    const networkOrAuth = data.type === Hls.ErrorTypes.NETWORK_ERROR;
    const status = data.response?.code;
    if (networkOrAuth && (status === 401 || status === 403)) {
      onFatalError({
        code: 'auth',
        message:
          'Сессия истекла или нет доступа к треку. Войдите заново и повторите попытку.',
      });
    } else if (networkOrAuth) {
      onFatalError({
        code: 'network',
        message: 'Сетевая ошибка при загрузке потока.',
      });
    } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
      onFatalError({ code: 'media', message: 'Ошибка декодирования трека.' });
    } else {
      onFatalError({
        code: 'unknown',
        message: 'Не удалось запустить воспроизведение.',
      });
    }
  };

  hls.on(Hls.Events.ERROR, handleHlsError);
  hls.attachMedia(audioEl);
  hls.loadSource(absoluteManifestUrl);

  return {
    kind: 'hls.js',
    destroy: () => {
      hls.off(Hls.Events.ERROR, handleHlsError);
      hls.destroy();
    },
  };
}

/**
 * Native-HLS attach (Safari macOS / iPad / iOS — BUG-161, ADR-024).
 *
 * Native `<audio src>` playback offers no request-header hook, so the loader
 * first mints a short-lived signed token via `fetchPreviewToken` and appends
 * it to the master URL as `?token=…`. The BE authorizes the whole preview
 * chain off that token (and re-stamps fresh chain tokens into the rewritten
 * manifest), so child playlists and the AES-128 key fetch need no header.
 *
 * The token fetch is async while this function must return a handle
 * synchronously — a `cancelled` flag ties the in-flight fetch to
 * `destroy()` so a track switch mid-fetch never sets a stale `src`.
 */
function attachNative(args: {
  audioEl: HTMLAudioElement;
  manifestUrl: string;
  fetchPreviewToken: () => Promise<string>;
  onFatalError: (err: HlsFatalError) => void;
}): HlsHandle {
  const { audioEl, manifestUrl, fetchPreviewToken, onFatalError } = args;
  let cancelled = false;

  const onAudioError = () => {
    onFatalError({
      code: 'network',
      message: 'Сетевая ошибка при загрузке потока.',
    });
  };
  audioEl.addEventListener('error', onAudioError);

  fetchPreviewToken()
    .then((token) => {
      if (cancelled) return;
      audioEl.src = `${manifestUrl}?token=${encodeURIComponent(token)}`;
    })
    .catch(() => {
      if (cancelled) return;
      onFatalError({
        code: 'auth',
        message:
          'Не удалось получить доступ к предпросмотру. Войдите заново и повторите попытку.',
      });
    });

  return {
    kind: 'native',
    destroy: () => {
      cancelled = true;
      audioEl.removeEventListener('error', onAudioError);
      audioEl.removeAttribute('src');
      audioEl.load();
    },
  };
}

/**
 * Build an hls.js `xhrSetup` hook that attaches the admin Bearer JWT only on
 * same-origin XHRs (BUG-066). Exported so it can be unit-tested in isolation
 * — production callers always go through {@link attachHls}.
 *
 * Cross-origin segment URLs (presigned S3) must NOT receive the Authorization
 * header: TimeWeb S3 signs only `host` and rejects an extra Authorization
 * with 400. `currentOrigin` defaults to `window.location.origin` and is
 * parameterised solely for testability in a node environment.
 */
export function attachAdminBearerToSameOrigin(
  accessToken: string,
  currentOrigin: string = typeof window !== 'undefined'
    ? window.location.origin
    : '',
): (xhr: XMLHttpRequest, url: string) => void {
  return (xhr, url) => {
    let target: URL;
    try {
      target = new URL(url, currentOrigin);
    } catch {
      /* malformed URL — skip injection */
      return;
    }
    if (target.origin === currentOrigin) {
      // setRequestHeader may throw InvalidStateError when hls.js calls
      // xhrSetup before xhr.open() — the error MUST propagate so hls.js
      // re-opens and re-invokes the hook (BUG-067).
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    }
  };
}
